import { useMemo, useState } from 'react'
import { matchPath, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { CopyDialog, DeleteConfirmDialog } from '@library'
import { DataGrid } from '@microservices/wiskey-react-table-component'
import { DRAFT_COLUMN, MOBILE_LIST_ITEMS_COLUMNS } from '@mobileConstants'
import { exportToJSON } from '@mobileHelpers'
import { useAllDrafts, useMobileListItems } from '@mobileHooks'
import { MobileListItem } from '@mobileTypes'
import { Box, Button, Typography } from '@mui/material'

import { PageContentLayout } from '@layouts/PageContentLayout'
import { PageTitle } from '@components/PageTitle'

import { generateDataGridActionsColumn } from '@helpers'
import { ROUTES } from '@constants'

export const ConfigMobileListItems = () => {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const isAllListItems = matchPath(ROUTES.CONFIG_MOBILE_LIST_ITEMS, pathname)

  const { listItems, loading, copyListItem, deleteListItem } = useMobileListItems()
  const { entries: draftEntries, deleteDraft } = useAllDrafts<MobileListItem>('mobile-list-item-draft-')

  const [copyDialogOpen, setCopyDialogOpen] = useState(false)
  const [itemToCopy, setItemToCopy] = useState<{ id: number; internalId: string; name: string } | null>(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [itemToDelete, setItemToDelete] = useState<{ internalId: string; name: string } | null>(null)

  const handleCreate = () => navigate(ROUTES.CONFIG_MOBILE_LIST_ITEMS_CREATE)

  const handleEdit = (id: number) => navigate(`${ROUTES.CONFIG_MOBILE_LIST_ITEMS_EDIT}/${id}`)

  const handleCopyClick = (id: number) => {
    const item = listItems.find(li => li.id === id)
    if (item) {
      setItemToCopy({ id: item.id, internalId: item.internalId, name: item.name })
      setCopyDialogOpen(true)
    }
  }

  const handleCopy = async (newName: string, newInternalId: string) => {
    if (itemToCopy) {
      const newItem = await copyListItem(itemToCopy.id, newName, newInternalId)
      if (newItem) {
        setCopyDialogOpen(false)
        setItemToCopy(null)
        navigate(`${ROUTES.CONFIG_MOBILE_LIST_ITEMS_EDIT}/${newItem.id}`)
      }
    }
  }

  const handleDeleteClick = (internalId: string) => {
    const item = listItems.find(li => li.internalId === internalId)
    if (item) {
      setItemToDelete({ internalId: item.internalId, name: item.name })
      setDeleteDialogOpen(true)
    }
  }

  const handleDelete = () => {
    if (itemToDelete) {
      deleteListItem(itemToDelete.internalId)
      setDeleteDialogOpen(false)
      setItemToDelete(null)
    }
  }

  const handleExport = (internalId: string) => {
    const item = listItems.find(li => li.internalId === internalId)
    if (item) exportToJSON(item, `${item.name}_list_item_config.json`)
  }

  const rows = useMemo(() => {
    const draftEntityIdMap = new Map(
      draftEntries.filter(e => e.idPart !== 'new').map(e => [e.idPart, e]),
    )

    const newDraftRows = draftEntries
      .filter(e => e.idPart === 'new')
      .map(e => ({
        ...e.draft.data,
        id: 'draft-new',
        _isDraft: true,
        _isNewDraft: true,
        _draftKey: e.key,
      }))

    const entityDraftRows = listItems
      .filter(li => draftEntityIdMap.has(String(li.id)))
      .map(li => {
        const entry = draftEntityIdMap.get(String(li.id))!
        return { ...li, ...entry.draft.data, id: li.id, _isDraft: true, _isNewDraft: false, _draftKey: entry.key }
      })

    const serverRows = listItems.filter(li => !draftEntityIdMap.has(String(li.id)))

    return [...newDraftRows, ...entityDraftRows, ...serverRows]
  }, [listItems, draftEntries])

  const columns = [
    DRAFT_COLUMN,
    ...MOBILE_LIST_ITEMS_COLUMNS,
    generateDataGridActionsColumn({
      onEdit: (params: any) => {
        if (params.row._isNewDraft) {
          navigate(ROUTES.CONFIG_MOBILE_LIST_ITEMS_CREATE, { state: { restoreDraft: true } })
        } else if (params.row._isDraft) {
          navigate(`${ROUTES.CONFIG_MOBILE_LIST_ITEMS_EDIT}/${params.row.id}`, { state: { restoreDraft: true } })
        } else {
          handleEdit(params.row.id)
        }
      },
      onCopy: (params: any) => {
        if (!params.row._isNewDraft) handleCopyClick(params.row.id)
      },
      onDelete: (params: any) => {
        if (params.row._isDraft) {
          deleteDraft(params.row._draftKey)
        } else {
          handleDeleteClick(params.row.internalId)
        }
      },
      onDowloadButtonClick: (params: any) => {
        if (!params.row._isNewDraft) handleExport(params.row.internalId)
      },
    }),
  ]

  return (
    <>
      <Outlet />

      <CopyDialog
        open={copyDialogOpen}
        title='Copy List Item Configuration'
        initialName={itemToCopy?.name || ''}
        existingNames={listItems.map(li => li.name)}
        initialInternalId={itemToCopy?.internalId}
        existingInternalIds={listItems.map(li => li.internalId)}
        onClose={() => { setCopyDialogOpen(false); setItemToCopy(null) }}
        onCopy={handleCopy}
      />

      <DeleteConfirmDialog
        open={deleteDialogOpen}
        title='Delete List Item Configuration'
        message='Are you sure you want to delete list item'
        itemName={itemToDelete?.name}
        onClose={() => { setDeleteDialogOpen(false); setItemToDelete(null) }}
        onConfirm={handleDelete}
      />

      <Box sx={{ ...(!isAllListItems && { display: 'none' }), flex: 1 }}>
        <PageContentLayout sx={{ p: 1 }}>
          <PageTitle
            end={false}
            start={
              <Typography display='inline' sx={{ mr: 1, ml: 1 }} variant='h6'>
                Mobile List Items
              </Typography>
            }
          />

          <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
            <Button variant='contained' onClick={handleCreate}>
              Create List Item Config
            </Button>
          </Box>

          <Box sx={{ display: 'flex', flex: 1 }}>
            <DataGrid rows={rows} columns={columns} loading={loading} hideFooter />
          </Box>
        </PageContentLayout>
      </Box>
    </>
  )
}
